<template>
  <b-card>
    <!-- Zones list selection -->
    <b-row>
      <b-col md="6">
        <ZonesList @zone-selected="onParentZoneSelected" />
      </b-col>
      <b-col md="6">
        <b-form-group label="Subzones" label-for="subzones">
          <v-select
            id="subzones"
            v-model="selectedSubZone"
            placeholder="Subzones"
            label="city"
            :options="subZonesPolygons"
            clearable
            :disabled="!selectedZone"
            @input="fetchSubZones"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Subzones UI -->
    <template v-if="selectedZone">
      <!-- Create button -->
      <div class="d-flex justify-content-between align-items-center my-2">
        <h5>Subzones for {{ selectedZone.split("::")[1] }}</h5>
        <b-button variant="primary" @click="onCreateNewSubzonePrice">
          Create Subzone price
        </b-button>
      </div>

      <!-- Loading -->
      <div v-if="fetchingSubzones" class="text-center my-3">
        Loading...
      </div>

      <!-- Empty state -->
      <div
        v-else-if="apiResponded && subZones.length === 0"
        class="text-center text-muted my-3"
      >
        No subzones found for this zone.
      </div>

      <!-- Table -->
      <b-table
        v-else-if="apiResponded && subZones.length > 0"
        :items="subZones"
        :fields="fields"
        bordered
        responsive
        small
      >
        <template #cell(label)="data">
          <div
            v-if="editingField === `label-${data.item.id}`"
            class="d-flex align-items-center"
          >
            <b-form-input
              v-model="editingValue"
              size="sm"
              class="mr-2"
              @keyup.enter="saveField(data.item.id, 'label')"
            />
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              @click="saveField(data.item.id, 'label')"
            >
              Save
            </b-button>
            <b-button size="sm" variant="secondary" @click="cancelEdit">
              Close
            </b-button>
          </div>
          <div
            v-else
            role="button"
            @click="startEdit(`label-${data.item.id}`, data.item.label)"
          >
            {{ data.item.label }}
          </div>
        </template>

        <template #cell(price_modifier)="data">
          <div
            v-if="editingField === `price_modifier-${data.item.id}`"
            class="d-flex align-items-center"
          >
            <b-form-input
              v-model="editingValue"
              type="number"
              size="sm"
              class="mr-2"
              @keyup.enter="saveField(data.item.id, 'price_modifier')"
            />
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              @click="saveField(data.item.id, 'price_modifier')"
            >
              Save
            </b-button>
            <b-button size="sm" variant="secondary" @click="cancelEdit">
              Close
            </b-button>
          </div>
          <div
            v-else
            role="button"
            @click="
              startEdit(
                `price_modifier-${data.item.id}`,
                data.item.price_modifier
              )
            "
          >
            {{ data.item.price_modifier }}
          </div>
        </template>

        <template #cell(start_time)="data">
          <div
            v-if="editingField === `start_time-${data.item.id}`"
            class="d-flex align-items-center"
          >
            <b-form-timepicker
              v-model="editingValue"
              size="sm"
              class="mr-2"
              @keyup.enter="saveField(data.item.id, 'start_time')"
            />
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              @click="saveField(data.item.id, 'start_time')"
            >
              Save
            </b-button>
            <b-button size="sm" variant="secondary" @click="cancelEdit">
              Close
            </b-button>
          </div>
          <div
            v-else
            role="button"
            @click="
              startEdit(`start_time-${data.item.id}`, data.item.start_time)
            "
          >
            {{ data.item.start_time }}
          </div>
        </template>

        <template #cell(end_time)="data">
          <div
            v-if="editingField === `end_time-${data.item.id}`"
            class="d-flex align-items-center"
          >
            <b-form-timepicker
              v-model="editingValue"
              size="sm"
              class="mr-2"
              @keyup.enter="saveField(data.item.id, 'end_time')"
            />
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              @click="saveField(data.item.id, 'end_time')"
            >
              Save
            </b-button>
            <b-button size="sm" variant="secondary" @click="cancelEdit">
              Close
            </b-button>
          </div>
          <div
            v-else
            role="button"
            @click="startEdit(`end_time-${data.item.id}`, data.item.end_time)"
          >
            {{ data.item.end_time }}
          </div>
        </template>

        <template #cell(days)="data">
          <div
            v-if="editingField === `days-${data.item.id}`"
            class="d-flex align-items-center"
          >
            <b-form-checkbox-group
              v-model="editingValue"
              :options="daysOptions"
              class="mr-2"
            />
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              @click="saveField(data.item.id, 'days')"
            >
              Save
            </b-button>
            <b-button size="sm" variant="secondary" @click="cancelEdit">
              Close
            </b-button>
          </div>
          <div
            v-else
            role="button"
            @click="startEdit(`days-${data.item.id}`, data.item.days)"
          >
            {{ formatDays(data.item.days) }}
          </div>
        </template>

        <template #cell(is_active)="data">
          <b-form-checkbox
            :checked="data.item.is_active"
            switch
            @change="toggleActive(data.item.id, $event)"
          />
        </template>
      </b-table>
      <div id="zones-polygons-map" />
    </template>

    <!-- Create Modal -->
    <b-modal
      v-model="showCreateModal"
      title="Create Subzone Override"
      hide-footer
    >
      <validation-observer ref="createObserver" v-slot="{ handleSubmit }">
        <b-form @submit.prevent="handleSubmit(onCreateSubmit)">
          <!-- Parent Zone (readonly) -->
          <b-form-group label="Parent Zone">
            <b-form-input :value="selectedZone" readonly />
          </b-form-group>

          <!-- Subzone -->
          <b-form-group label="Subzone">
            <validation-provider
              v-slot="{ errors }"
              name="Subzone"
              rules="required"
            >
              <!-- <b-form-input
                v-model="form.subzone"
                :state="errors.length ? false : null"
                placeholder="Enter subzone name"
              /> -->
              <v-select
                v-model="form.subzone"
                placeholder="Subzone"
                label="city"
                :reduce="(opt) => opt.city"
                :options="subZonesPolygons"
                :disabled="!!selectedSubZone"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Label -->
          <b-form-group label="Label">
            <validation-provider
              v-slot="{ errors }"
              name="Label"
              rules="required"
            >
              <b-form-input
                v-model="form.label"
                :state="errors.length ? false : null"
                placeholder="Enter label"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Price Modifier -->
          <b-form-group label="Price Modifier">
            <validation-provider
              v-slot="{ errors }"
              name="Price Modifier"
              rules="required"
            >
              <b-form-input
                v-model.number="form.price_modifier"
                type="number"
                step="0.01"
                :state="errors.length ? false : null"
                placeholder="Enter price modifier"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Start Time -->
          <b-form-group label="Start Time">
            <validation-provider
              v-slot="{ errors }"
              name="Start Time"
              rules="required"
            >
              <b-form-timepicker
                v-model="form.start_time"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- End Time -->
          <b-form-group label="End Time">
            <validation-provider
              v-slot="{ errors }"
              name="End Time"
              rules="required"
            >
              <b-form-timepicker
                v-model="form.end_time"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Days -->
          <b-form-group label="Days">
            <validation-provider
              v-slot="{ errors }"
              name="Days"
              rules="required"
            >
              <b-form-checkbox-group
                v-model="form.days"
                :options="daysOptions"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Active Switch -->
          <b-form-group label="Active">
            <b-form-checkbox v-model="form.is_active" switch>
              Active
            </b-form-checkbox>
          </b-form-group>

          <!-- Actions -->
          <div class="d-flex justify-content-end">
            <b-button
              variant="secondary"
              class="mr-1"
              :disabled="createLoading"
              @click="showCreateModal = false"
            >
              Cancel
            </b-button>
            <b-button type="submit" variant="primary" :disabled="createLoading">
              <span v-if="createLoading">
                <b-spinner small type="border" class="mr-1" /> Saving...
              </span>
              <span v-else>
                Save
              </span>
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
/* eslint-disable no-undef */
import { ref } from "@vue/composition-api";
import {
  BCard,
  BTable,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BSpinner,
  BRow,
  BCol,
  BFormTimepicker,
  BFormCheckboxGroup,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import vSelect from "vue-select";
import ZonesList from "../versions/components/ZonesList.vue";
import axiosIns from "@/libs/axios";
import { errorNotification } from "@/utils/toast-notifications";

export default {
  components: {
    ZonesList,
    BCard,
    BTable,
    vSelect,
    BButton,
    BModal,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BSpinner,
    BFormTimepicker,
    BFormCheckboxGroup,
    ValidationObserver,
    ValidationProvider,
  },
  setup() {
    const selectedZone = ref("");
    const fetchingSubzones = ref(false);
    const subZones = ref([]);
    const apiResponded = ref(false);
    const showCreateModal = ref(false);
    const createLoading = ref(false);
    const subZonesPolygons = ref([]);
    const selectedSubZone = ref();
    const editingField = ref("");
    const editingValue = ref("");

    const form = ref({
      subzone: "",
      label: "",
      price_modifier: null,
      is_active: true,
      start_time: "09:00:00",
      end_time: "17:00:00",
      days: [],
    });

    const fields = [
      { key: "label", label: "Label" },
      { key: "subzone", label: "Subzone" },
      { key: "price_modifier", label: "Price Modifier" },
      { key: "start_time", label: "Start Time" },
      { key: "end_time", label: "End Time" },
      { key: "days", label: "Days" },
      { key: "is_active", label: "Active" },
    ];

    const daysOptions = [
      {
        text: "Sunday",
        value: 6,
      },
      {
        text: "Monday",
        value: 0,
      },
      {
        text: "Tuesday",
        value: 1,
      },
      {
        text: "Wednesday",
        value: 2,
      },
      {
        text: "Thursday",
        value: 3,
      },
      {
        text: "Friday",
        value: 4,
      },
      {
        text: "Saturday",
        value: 5,
      },
    ];

    const map = ref(null);
    const polygonsRefs = ref([]);

    const formatDays = (days) => {
      if (!days || !Array.isArray(days) || days.length === 0) {
        return "No days selected";
      }
      const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      return days
        .map((day) => dayNames[day])
        .sort((a, b) => {
          const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
          return order.indexOf(a) - order.indexOf(b);
        })
        .join(", ");
    };

    const handlePolygonChange = (subzone, polygon) => {
      const newCoords = polygon
        .getPath()
        .getArray()
        .map((latlng) => latlng.toJSON());
      console.log("Updated polygon for", subzone.name, newCoords);
      // Optionally send to API here...
    };

    const generateColors = (count) => {
      const colors = [];
      for (let i = 0; i < count; i += 1) {
        // evenly spread hue values for better differentiation
        const hue = Math.floor((360 / count) * i);
        colors.push(`hsl(${hue}, 70%, 50%)`);
      }
      return colors;
    };

    const initMap = () => {
      if (!window.google) {
        console.error("Google Maps not loaded");
        return;
      }

      map.value = new google.maps.Map(
        document.getElementById("zones-polygons-map"),
        {
          zoom: 10,
          center: { lat: 31.963158, lng: 35.930359 },
        }
      );

      polygonsRefs.value.forEach((poly) => poly.setMap(null));
      polygonsRefs.value = [];

      const infoWindow = new google.maps.InfoWindow();
      const bounds = new google.maps.LatLngBounds();

      // Generate distinct colors
      const colors = generateColors(subZonesPolygons.value.length);

      subZonesPolygons.value.forEach((subzone, index) => {
        const color = colors[index % colors.length]; // cycle if more than generated
        const polygon = new google.maps.Polygon({
          paths: subzone.polygon,
          strokeColor: color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.35,
        });

        polygon.setMap(map.value);
        polygonsRefs.value.push(polygon);

        subzone.polygon.forEach((coord) => {
          bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
        });

        google.maps.event.addListener(polygon.getPath(), "set_at", () => {
          handlePolygonChange(subzone, polygon);
        });
        google.maps.event.addListener(polygon.getPath(), "insert_at", () => {
          handlePolygonChange(subzone, polygon);
        });

        google.maps.event.addListener(polygon, "click", (event) => {
          // Find the corresponding subzone data with price modifier
          const matchingSubzone = subZones.value.find(
            (sz) => sz.subzone === subzone.city
          );

          const content = `
        <div style="min-width:150px">
          <strong>${subzone.name}</strong><br/>
          ${subzone.nameAr ? `<span>${subzone.nameAr}</span><br/>` : ""}
          City: ${subzone.city}<br/>
          Status: ${subzone.status}<br/>
          ${
            matchingSubzone
              ? `Price Modifier: ${matchingSubzone.price_modifier}<br/>`
              : ""
          }
          ${matchingSubzone ? `Label: ${matchingSubzone.label}<br/>` : ""}
          ${
            matchingSubzone
              ? `Active: ${matchingSubzone.is_active ? "Yes" : "No"}`
              : ""
          }
        </div>
      `;
          infoWindow.setContent(content);
          infoWindow.setPosition(event.latLng);
          infoWindow.open(map.value);
        });
      });

      if (!bounds.isEmpty()) {
        map.value.fitBounds(bounds);
      }
    };

    const fetchSubZones = async () => {
      try {
        fetchingSubzones.value = true;
        apiResponded.value = false;

        const result = await axiosIns.get("pricing/subzone-overrides", {
          params: {
            parent_zone: selectedZone.value,
            subzone: selectedSubZone?.value?.city,
          },
        });
        subZones.value = result.data.data;

        const subZonesResult = await axiosIns.get("/zones/sub-zones", {
          params: {
            parent: selectedZone.value,
            type: "sub-zone",
            city: selectedSubZone.value?.city || undefined,
          },
        });

        subZonesPolygons.value = subZonesResult.data.data;
        initMap();
      } finally {
        fetchingSubzones.value = false;
        apiResponded.value = true;
      }
    };

    const onParentZoneSelected = (parentZone) => {
      selectedZone.value = parentZone;
      fetchSubZones();
    };

    const onCreateSubmit = async () => {
      createLoading.value = true;
      try {
        const payload = {
          parent_zone: selectedZone.value,
          subzone: form.value.subzone,
          label: form.value.label,
          price_modifier: form.value.price_modifier,
          is_active: form.value.is_active,
          start_time: form.value.start_time,
          end_time: form.value.end_time,
          days: form.value.days,
        };
        await axiosIns.post("/pricing/subzone-overrides/", payload);
        showCreateModal.value = false;
        fetchSubZones();
        form.value = {
          subzone: "",
          label: "",
          price_modifier: null,
          is_active: true,
          start_time: "09:00:00",
          end_time: "17:00:00",
          days: [],
        };
      } catch (err) {
        const errMsg = err?.response?.data?.error?.detail?.message;
        errorNotification(errMsg || "An error occured");
        console.log(err.response);
      } finally {
        createLoading.value = false;
      }
    };

    const onCreateNewSubzonePrice = () => {
      if (selectedSubZone.value) {
        form.value.subzone = selectedSubZone.value.city;
      }
      showCreateModal.value = true;
    };

    const startEdit = (fieldKey, currentValue) => {
      editingField.value = fieldKey;
      editingValue.value = currentValue;
    };

    const cancelEdit = () => {
      editingField.value = "";
      editingValue.value = "";
    };

    const saveField = async (itemId, fieldName) => {
      try {
        let value = editingValue.value;

        // Convert to appropriate type based on field
        if (fieldName === "price_modifier") {
          value = parseFloat(editingValue.value);
        } else if (fieldName === "days") {
          // For days, ensure it's an array
          value = Array.isArray(editingValue.value) ? editingValue.value : [];
        }
        // For start_time and end_time, keep as string (already handled by timepicker)

        const payload = { [fieldName]: value };
        await axiosIns.put(`/pricing/subzone-overrides/${itemId}`, payload);

        // Update the local data
        const itemIndex = subZones.value.findIndex(
          (item) => item.id === itemId
        );
        if (itemIndex !== -1) {
          subZones.value[itemIndex][fieldName] = value;
        }

        cancelEdit();
      } catch (err) {
        const errMsg = err?.response?.data?.error?.detail?.message;
        errorNotification(errMsg || "An error occurred");
        console.log(err.response);
      }
    };

    const toggleActive = async (itemId, isActive) => {
      try {
        await axiosIns.put(`/pricing/subzone-overrides/${itemId}`, {
          is_active: isActive,
        });

        // Update the local data
        const itemIndex = subZones.value.findIndex(
          (item) => item.id === itemId
        );
        if (itemIndex !== -1) {
          subZones.value[itemIndex].is_active = isActive;
        }
      } catch (err) {
        const errMsg = err?.response?.data?.error?.detail?.message;
        errorNotification(errMsg || "An error occurred");
        console.log(err.response);
      }
    };

    return {
      fetchingSubzones,
      selectedZone,
      subZonesPolygons,
      subZones,
      fields,
      selectedSubZone,
      onCreateNewSubzonePrice,
      apiResponded,
      showCreateModal,
      form,
      createLoading,
      onParentZoneSelected,
      onCreateSubmit,
      required,
      fetchSubZones,
      editingField,
      editingValue,
      startEdit,
      cancelEdit,
      saveField,
      toggleActive,
      daysOptions,
      formatDays,
    };
  },
};
</script>

<style scoped>
#zones-polygons-map {
  width: 100%;
  height: 500px;
}

/* Inline editing styles */
.table td div[role="button"] {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.table td div[role="button"]:hover {
  background-color: #f8f9fa;
}

.table td .d-flex {
  min-height: 32px;
  align-items: center;
}

.table td .d-flex .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}
</style>
